<template>
	<SuiDatePicker
		:value="modelValue"
		:name="name"
		:format="format"
		:placeholder="placeholder"
		:label="label"
		:disabled="disabled"
		:invalid="invalid"
		:auto-navigation="autoNavigation"
		:date-disabled="disabledDates"
		:weekdays-short="weekdaysShort"
		:months="months"
		:months-short="monthsShort"
		:first-day-of-week="firstDayOfWeek"
		:size="size"
		:variant="variant"
		:message="message"
		:append-to-body="appendToBody"
		:hide-clear-button="hideClearButton"
		:clear-button-label="clearButtonLabel"
		:restore-date-on-close="restoreDateOnClose"
		@input="$emit('update:model-value', $event)"
	/>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { SuiDatePicker, type SuiInputVariant } from '@developers/scoro-ui';
import { useTranslation } from '@/composables';
import { useSettings } from '@/composables/settings/useSettings';
import { Constants } from '@/constants';
import { WEEK_STARTS_ON } from '@/constants/settings';
import { type ValueOf } from '@/types';

interface AppDatePickerProps {
	modelValue?: string;
	name?: string;
	format?: string;
	placeholder?: string;
	label?: string;
	disabled?: boolean;
	invalid?: boolean;
	size?: string;
	dateDisabled?: (date: string) => boolean;
	checkWeekends?: boolean;
	autoNavigation?: boolean;
	hideClearButton?: boolean;
	clearButtonLabel?: string;
	variant?: ValueOf<typeof SuiInputVariant>;
	message?: string;
	appendToBody?: boolean;
	restoreDateOnClose?: boolean;
}

const props = defineProps<AppDatePickerProps>();

defineEmits(['update:model-value']);

const { dateDisabled } = toRefs(props);

// TO NOTE: vue datepicker is created so that day 0 of week is Sunday
const VUE_DATEPICKER_SATURDAY = 6;
const VUE_DATEPICKER_SUNDAY = 0;
const VUE_DATEPICKER_MONDAY = 1;

const { t } = useTranslation();
const { setting } = useSettings();

const weekdaysShort = [
	t('wday_short_7'),
	t('wday_short_1'),
	t('wday_short_2'),
	t('wday_short_3'),
	t('wday_short_4'),
	t('wday_short_5'),
	t('wday_short_6'),
];

const months = [
	t('month_1'),
	t('month_2'),
	t('month_3'),
	t('month_4'),
	t('month_5'),
	t('month_6'),
	t('month_7'),
	t('month_8'),
	t('month_9'),
	t('month_10'),
	t('month_11'),
	t('month_12'),
];

const monthsShort = [
	t('month_short_1'),
	t('month_short_2'),
	t('month_short_3'),
	t('month_short_4'),
	t('month_short_5'),
	t('month_short_6'),
	t('month_short_7'),
	t('month_short_8'),
	t('month_short_9'),
	t('month_short_10'),
	t('month_short_11'),
	t('month_short_12'),
];

const firstDayOfWeek = computed(() => {
	const weekStartsOn = parseInt(
		setting(Constants.Setting.WEEK_STARTS_ON) || ''
	);

	switch (weekStartsOn) {
		case WEEK_STARTS_ON.SUNDAY:
			return VUE_DATEPICKER_SUNDAY;
		case WEEK_STARTS_ON.SATURDAY:
			return VUE_DATEPICKER_SATURDAY;
		default:
			return VUE_DATEPICKER_MONDAY;
	}
});

const includeWeekendsInScheduleSetting = computed(
	() =>
		!!parseInt(
			setting(Constants.Setting.INCLUDE_WEEKENDS_WHEN_SCHEDULING) || ''
		)
);

const disabledDates = (date: string) => {
	const disableWeekends = () => {
		if (props.checkWeekends && !includeWeekendsInScheduleSetting.value) {
			return [VUE_DATEPICKER_SUNDAY, VUE_DATEPICKER_SATURDAY].includes(
				new Date(date).getDay()
			);
		}

		return false;
	};

	return (
		disableWeekends() ||
		(dateDisabled.value && dateDisabled.value(date)) ||
		false
	);
};
</script>
